import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { apiFetch } from '@/utils/api';
import { formatCurrency, formatNumber } from '@/utils/formatters';
import {
    ArrowsRightLeftIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface InventoryTransaction {
    id: string;
    transaction_type: string;
    product_id: string;
    cost_center_id: string;
    batch_id?: string;
    quantity: number;
    unit_cost?: number;
    total_cost?: number;
    reference_type?: string;
    reference_id?: string;
    notes?: string;
    created_by?: string;
    created_at: string;
    transaction_date: string;
}

interface Product {
    id: string;
    name: string;
    manufacturer?: string;
}

interface Location {
    id: string;
    name: string;
}

export default function InventoryTransactionsPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [selectedProduct, setSelectedProduct] = useState<string>('');
    const [selectedLocation, setSelectedLocation] = useState<string>('');
    const [selectedTransactionType, setSelectedTransactionType] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    const transactionTypes = [
        { value: 'receipt', label: 'Receipt', color: 'green' },
        { value: 'issue', label: 'Issue', color: 'red' },
        { value: 'adjustment', label: 'Adjustment', color: 'yellow' },
        { value: 'transfer', label: 'Transfer', color: 'blue' },
        { value: 'return', label: 'Return', color: 'orange' },
        { value: 'waste', label: 'Waste', color: 'gray' }
    ];

    useEffect(() => {
        fetchData();
    }, [selectedProduct, selectedLocation, selectedTransactionType]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters
            const params = new URLSearchParams();
            if (selectedProduct) params.append('product_id', selectedProduct);
            if (selectedLocation) params.append('cost_center_id', selectedLocation);
            if (selectedTransactionType) params.append('transaction_type', selectedTransactionType);
            params.append('limit', '100');

            const [transactionsResponse, productsResponse, locationsResponse] = await Promise.all([
                apiFetch(`/api/inventory/transactions/?${params.toString()}`),
                apiFetch('/api/products/?limit=100'),
                apiFetch('/api/cost-centers/?is_active=true&limit=100')
            ]);

            if (transactionsResponse.ok && productsResponse.ok && locationsResponse.ok) {
                const transactionsData = await transactionsResponse.json();
                const productsData = await productsResponse.json();
                const locationsData = await locationsResponse.json();

                setTransactions(transactionsData);
                setProducts(productsData.products || productsData);
                setLocations(locationsData);
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load transaction data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const filteredTransactions = transactions.filter(transaction =>
        searchTerm === '' ||
        getProductName(transaction.product_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getLocationName(transaction.cost_center_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.notes?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getProductName = (productId: string) => {
        const product = products.find(p => p.id === productId);
        return product ? `${product.name}${product.manufacturer ? ` (${product.manufacturer})` : ''}` : 'Unknown Product';
    };

    const getLocationName = (locationId: string) => {
        const location = locations.find(l => l.id === locationId);
        return location?.name || 'Unknown Location';
    };

    const getTransactionTypeInfo = (type: string) => {
        return transactionTypes.find(t => t.value === type) || { value: type, label: type, color: 'gray' };
    };

    const formatTransactionDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Transaction History</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            View all inventory movements and transactions
                        </p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div>
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search transactions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                />
                            </div>
                        </div>

                        <select
                            value={selectedProduct}
                            onChange={(e) => setSelectedProduct(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Products</option>
                            {products.map((product) => (
                                <option key={product.id} value={product.id}>
                                    {product.name} {product.manufacturer && `(${product.manufacturer})`}
                                </option>
                            ))}
                        </select>

                        <select
                            value={selectedLocation}
                            onChange={(e) => setSelectedLocation(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Locations</option>
                            {locations.map((location) => (
                                <option key={location.id} value={location.id}>
                                    {location.name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={selectedTransactionType}
                            onChange={(e) => setSelectedTransactionType(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">All Types</option>
                            {transactionTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>

                        <button
                            onClick={() => {
                                setSelectedProduct('');
                                setSelectedLocation('');
                                setSelectedTransactionType('');
                                setSearchTerm('');
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Product</TableHead>
                                <TableHead>Location</TableHead>
                                <TableHead>Quantity</TableHead>
                                <TableHead>Cost</TableHead>
                                <TableHead>Notes</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredTransactions.map((transaction) => {
                                const typeInfo = getTransactionTypeInfo(transaction.transaction_type);
                                const isPositive = transaction.quantity > 0;

                                return (
                                    <TableRow key={transaction.id}>
                                        <TableCell>
                                            {formatTransactionDate(transaction.transaction_date)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                typeInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                                                typeInfo.color === 'red' ? 'bg-red-100 text-red-800' :
                                                typeInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                                typeInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                                                typeInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {isPositive ? (
                                                    <ArrowUpIcon className="h-3 w-3 mr-1" />
                                                ) : (
                                                    <ArrowDownIcon className="h-3 w-3 mr-1" />
                                                )}
                                                {typeInfo.label}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {getProductName(transaction.product_id)}
                                        </TableCell>
                                        <TableCell>
                                            {getLocationName(transaction.cost_center_id)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`text-sm font-medium ${
                                                isPositive ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {isPositive ? '+' : ''}{formatNumber(transaction.quantity)}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {transaction.total_cost ? formatCurrency(transaction.total_cost) :
                                                transaction.unit_cost ? formatCurrency(transaction.unit_cost) + '/unit' : '-'}
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate">
                                            {transaction.notes || '-'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    {filteredTransactions.length === 0 && (
                        <div className="text-center py-12">
                            <ArrowsRightLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm || selectedProduct || selectedLocation || selectedTransactionType
                                    ? 'No transactions match your current filters.'
                                    : 'No inventory transactions have been recorded yet.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/transactions')({
  component: InventoryTransactionsPage,
})
